import React, { HTMLAttributes } from "react";
import classNames from "classnames";

import {
  ToggleWrapper,
  ToggleComp,
  ToggleCheckbox,
  ToggleLabel,
  ToggleSwitch,
  ToggleTextFalse,
  ToggleTextTrue
} from "./Toggle.styled";
import { PropsWithDataQaid } from "src/types/componentProps";

export type ToggleProps = HTMLAttributes<HTMLInputElement> &
  PropsWithDataQaid<{
    /**
     * Set toggle to be checked
     */
    checked: boolean;

    /**
     * Set toggle to be disabled
     */
    disabled?: boolean;

    /**
     * Set toggle labels (array of strings of labels)
     */
    labels: string[];

    /**
     * Set toggle colow
     *
     * @type {"orange" | "green"}
     * @default "green"
     */
    color?: "orange" | "green";

    /**
     * Set toggle size
     *
     * @type {"big" | "small"}
     * @default "big"
     */
    size?: "big" | "small";
  }>;

const defaultProps: ToggleProps = {
  checked: false,
  color: "green",
  disabled: false,
  labels: [] as string[],
  size: "big"
};

const Toggle = React.forwardRef<HTMLDivElement, ToggleProps>((props, ref) => {
  const { className, checked, color, disabled, labels, size, "data-qaid": dataQaid, ...restProps } = props;
  const toggleClass = classNames("c-toggle", disabled && "is-disabled", className);

  if (labels.length > 0) {
    return (
      <ToggleWrapper ref={ref} className={toggleClass} data-testid="qa-toggle-wrapper">
        <ToggleTextFalse
          data-testid="qa-toggle__label-false"
          className="c-toggle__text-false"
          checked={checked}
          disabled={disabled}
        >
          {labels[0]}
        </ToggleTextFalse>
        <ToggleComp size={size} data-qaid={dataQaid} className="c-toggle__comp">
          <ToggleLabel data-testid="qa-toggle__label" className="c-toggle__label">
            <ToggleCheckbox
              type="checkbox"
              className="c-toggle__checkbox"
              data-testid="qa-toggle__checkbox"
              checked={checked}
              disabled={disabled}
              {...restProps}
            />
            <ToggleSwitch
              color={color}
              size={size}
              data-testid="qa-toggle__switch"
              className="c-toggle__switch"
              checked={checked}
              disabled={disabled}
            />
          </ToggleLabel>
        </ToggleComp>
        <ToggleTextTrue
          data-testid="qa-toggle__label-true"
          className="c-toggle__text-true"
          checked={checked}
          disabled={disabled}
        >
          {labels[1]}
        </ToggleTextTrue>
      </ToggleWrapper>
    );
  }

  return (
    /* `data-qaid` is rendered here as well as on the labelled branch above: without it a Toggle
       used without `labels` silently dropped the caller's id. No default, so an unset prop still
       renders no attribute. */
    <ToggleComp
      ref={ref}
      className={toggleClass}
      size={size}
      data-qaid={dataQaid}
      data-testid="qa-toggle-wrapper-no-label"
    >
      <ToggleLabel data-testid="qa-toggle__label" className="c-toggle__label">
        <ToggleCheckbox
          type="checkbox"
          className="c-toggle__checkbox"
          data-testid="qa-toggle__checkbox"
          checked={checked}
          disabled={disabled}
          {...restProps}
        />
        <ToggleSwitch
          color={color}
          size={size}
          data-testid="qa-toggle__switch"
          className="c-toggle__switch"
          checked={checked}
          disabled={disabled}
        />
      </ToggleLabel>
    </ToggleComp>
  );
});

Toggle.defaultProps = defaultProps;

export default Toggle;
